import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import { Navigate } from 'react-router-dom';
import axios from "axios";
import { Button, Box, Card, Stack, Container, Typography, Chip } from '@mui/material';
import { LoadScript } from '@react-google-maps/api';

import ZoneControl from '../components/ZoneControl';
import LampControl from '../components/LampControl';
import GoogleMapZoneComponent from '../components/GoogleMapZoneComponent';
import mqtt from 'mqtt';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';

import { Grid } from '@mui/material';

import {
  AppWidgetSummary,
  AppCurrentVisits,
} from '../sections/@dashboard/app';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

import ZoneDashboard from '../components/ZoneDashboard';
import ZoneDashboardCounters from '../components/ZoneDashboardCounters';
import ZoneDashboardDiagram from '../components/ZoneDashboardDiagram';

import { useLocation } from 'react-router-dom';

import { useRef } from 'react';

export default function HuntersReportAll() {
  const [zones, setZones] = useState([]);
  const [lampsByZone, setLampsByZone] = useState({});
  const [selectedLamp, setSelectedLamp] = useState(null);
  const [selectedZoneId, setSelectedZoneId] = useState(null);

  const [zoneFilters, setZoneFilters] = useState({});
  const [deleteZone, setDeleteZone] = useState(null);

  const { user: currentUser } = useSelector((state) => state.auth);

  const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

  const navigate = useNavigate();

  const location = useLocation();

  const targetZoneId = location.state?.zoneId;

  const zoneRefs = useRef({});

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (!targetZoneId) return;

    const timer = setTimeout(() => {
      const el = zoneRefs.current[targetZoneId];
      if (!el) return;

      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 1000); // ⏱️ 5 sekundi

    return () => clearTimeout(timer); // cleanup
  }, [zones.length, targetZoneId]);

  useEffect(() => {
    setSelectedLamp(null);
    setSelectedZoneId(null);
  }, []);

  useEffect(() => {
    const client = mqtt.connect('wss://smartlight.bhtelecom.ba:8083');

    client.on('connect', () => {
      client.subscribe('/smartmap/updates/#', { qos: 1 });
    });

    client.on('message', (topic, message) => {
      const data = JSON.parse(message.toString());

      setLampsByZone(prev => {
        const updated = { ...prev };

        Object.keys(updated).forEach(zoneId => {
          updated[zoneId] = updated[zoneId].map(lamp =>
            lamp.devEUNumber === data.devEUNumber
              ? {
                ...lamp,
                status: data.status,
                currentDimming: data.bri ?? data.dimming ?? lamp.currentDimming
              }
              : lamp
          );
        });

        return updated;
      });
    });

    return () => client.end(true);
  }, []);

  useEffect(() => {
    getAllZones();
  }, []);

  useEffect(() => {
    zones.forEach(zone => {
      getLampsForZone(zone.id);
    });
  }, [zones]);

  const getAllZones = async () => {
    try {
      const response = await axios.get(`${SERVER_URL}/api/zonas`);
      setZones(response.data);
    } catch (err) {
      console.log("error", err);
    }
  };

  const getLampsForZone = async (zoneId) => {
    try {
      const res = await axios.get(`${SERVER_URL}/api/streetlamps/by-zone/${zoneId}`);
      setLampsByZone(prev => ({ ...prev, [zoneId]: res.data }));
    } catch (err) {
      console.error(err);
    }
  };

  const companyStats = zones.reduce((acc, z) => {
    acc.total += z.numbertotal || 0;
    acc.active += z.numberaktivne || 0;
    acc.inactive += z.numberneaktivne || 0;
    acc.alarms += z.numberalarms || 0;
    acc.failed += z.numberfailed || 0;
    return acc;
  }, {
    total: 0,
    active: 0,
    inactive: 0,
    alarms: 0,
    failed: 0
  });

  const companyCharts = zones.reduce((acc, z) => {
    acc.number45w += z.number45w || 0;
    acc.number60w += z.number60w || 0;
    acc.number90w += z.number90w || 0;

    acc.numberaktivne += z.numberaktivne || 0;
    acc.numberneaktivne += z.numberneaktivne || 0;

    acc.januar += z.januar || 0;
    acc.februar += z.februar || 0;
    acc.mart += z.mart || 0;
    acc.april += z.april || 0;
    acc.maj += z.maj || 0;
    acc.juni += z.juni || 0;
    acc.juli += z.juli || 0;
    acc.august += z.august || 0;
    acc.septembar += z.septembar || 0;
    acc.oktobar += z.oktobar || 0;
    acc.novembar += z.novembar || 0;
    acc.decembar += z.decembar || 0;

    return acc;
  }, {
    number45w: 0,
    number60w: 0,
    number90w: 0,
    numberaktivne: 0,
    numberneaktivne: 0,
    januar: 0,
    februar: 0,
    mart: 0,
    april: 0,
    maj: 0,
    juni: 0,
    juli: 0,
    august: 0,
    septembar: 0,
    oktobar: 0,
    novembar: 0,
    decembar: 0
  });

  if (!currentUser) return <Navigate to="/login" />;

  return (
    <>
      <Helmet>
        <title>Pametna rasvjeta</title>
      </Helmet>

      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%', // 🔥 OVO
          backgroundColor: '#001536'
        }}>
          <Container maxWidth={false} sx={{ p: 2 }}>
            <Stack spacing={2}>
              {zones.map((row) => (
                <Card
                  key={row.id}
                  ref={(el) => (zoneRefs.current[row.id] = el)}
                  sx={{ p: 2, backgroundColor: '#00122E', color: '#fff', scrollMarginTop: '200px'}}
                >

                  {/* HEADER */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '1.5rem',   // 🔥 veće slovo
                        color: '#ff9800'      // 🔥 orange
                      }}
                    >
                      {row.name}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1
                      }}
                    >
                      <Button
                        sx={{
                          minWidth: 100,
                          backgroundColor: '#001B44',
                          color: '#ff9800',                // 🔥 tekst orange
                          border: '1px solid #ff9800',     // 🔥 border orange
                          '&:hover': {
                            backgroundColor: '#001B44',
                            border: '1px solid #ffa726',   // 🔥 malo svjetliji hover
                            color: '#ffa726'
                          }
                        }}
                        onClick={() => navigate("/zone/update", { state: row })}
                      >
                        Uredi
                      </Button>

                      <Button
                        sx={{
                          minWidth: 100,
                          backgroundColor: '#001B44',
                          color: '#ff9800',                // 🔥 tekst orange
                          border: '1px solid #ff9800',     // 🔥 border orange
                          '&:hover': {
                            backgroundColor: '#001B44',
                            border: '1px solid #ffa726',   // 🔥 malo svjetliji hover
                            color: '#ffa726'
                          }
                        }}
                        onClick={() => setDeleteZone(row)}
                      >
                        Obriši
                      </Button>
                    </Box>
                  </Box>

                  {/* RESPONSIVE TOP SECTION */}
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2
                    }}
                  >

                    {/* LEFT INFO */}
                    <Box
                      sx={{
                        flex: '0 0 400px',
                        maxWidth: '400px',
                        p: 2,
                        border: '1px solid #3949ab33',
                        borderRadius: 2,
                        backgroundColor: '#0f172a'
                      }}
                    >
                      <Typography sx={{ color: '#ff9800' }}>Tip: {row.type}</Typography>
                      <Typography sx={{ color: '#ff9800' }}>Klasifikacija: {row.classification}</Typography>
                      <Typography sx={{ color: '#ff9800' }}>Regija: {row.region}</Typography>
                      <Typography sx={{ color: '#ff9800' }}>Režim rada: {row.lightingmode?.name}</Typography>
                    </Box>

                    {/* CENTER CONTROL */}
                    <Box
                      sx={{
                        flex: '1 1 300px',
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          border: '1px solid #3949ab33',
                          borderRadius: 2,
                          backgroundColor: '#0f172a',
                          width: '100%',
                          maxWidth: '400px'
                        }}
                      >
                        <ZoneControl
                          zoneid={row.id}
                          lamps={lampsByZone[row.id] || []}
                          onFilterChange={(filter) => {
                            setZoneFilters(prev => ({ ...prev, [row.id]: filter }));
                          }}
                        />
                      </Box>
                    </Box>

                    {/* RIGHT INFO */}
                    <Box
                      sx={{
                        p: 1,
                        border: '1px solid #3949ab33',
                        borderRadius: 2,
                        backgroundColor: '#0f172a'
                      }}
                    >
                      <ZoneDashboardDiagram zonaId={row.id} width={375} height={280} />
                    </Box>
                  </Box>

                  {/* BOTTOM SECTION */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>

                    {/* LIST */}
                    <Card sx={{ flex: '1 1 350px', p: 2, backgroundColor: '#00122E', color: '#ff9800' }}>
                      <Typography sx={{ mb: 1 }}>Lampe u zoni</Typography>

                      <Box sx={{ height: '580px', overflowY: 'auto' }}>
                        {(lampsByZone[row.id] || [])
                          .filter(lamp => {
                            const filter = zoneFilters[row.id];
                            if (!filter || filter === "all") return true;
                            return lamp.status === filter;
                          })
                          .map(lamp => (
                            <Box
                              key={lamp.id}
                              sx={{
                                border: '1px solid #3949ab33',
                                borderRadius: 2,
                                p: 2,
                                mb: 1.5,
                                display: 'flex',
                                justifyContent: 'space-between',
                                backgroundColor: '#0f172a'
                              }}
                            >

                              <Box>
                                <Typography>Lampa: {lamp.name}</Typography>
                                <Typography sx={{ fontSize: '12px' }}>{lamp.installationAddress}</Typography>

                                <Chip
                                  label={
                                    lamp.status === 'active'
                                      ? 'Uključeno'
                                      : lamp.status === 'inactive'
                                        ? 'Isključeno'
                                        : lamp.status === 'alarm'
                                          ? 'Pod alarmom'
                                          : lamp.status
                                  }
                                  size="small"
                                  sx={{
                                    mt: 1,
                                    backgroundColor:
                                      lamp.status === 'active'
                                        ? '#facc15'
                                        : lamp.status === 'inactive'
                                          ? '#6b7280'
                                          : '#dc2626'
                                  }}
                                />

                                <Button
                                  size="small"
                                  sx={{ mt: 1 }}
                                  onClick={() => {
                                    setSelectedLamp(lamp);
                                    setSelectedZoneId(row.id);

                                    setTimeout(() => {
                                      setSelectedLamp(null);
                                      setSelectedZoneId(null);
                                    }, 2000);
                                  }}
                                >
                                  Locate
                                </Button>
                              </Box>

                              <LampControl lamp={lamp} />

                            </Box>
                          ))}
                      </Box>
                    </Card>

                    {/* MAP */}
                    <Card sx={{ flex: '2 1 500px', height: '635px' }}>
                      <GoogleMapZoneComponent
                        key={location.key}
                        zonaid={row.id}
                        selectedLamp={selectedLamp}
                        selectedZoneId={selectedZoneId}
                        filter={zoneFilters[row.id]}
                      />
                    </Card>

                  </Box>
                </Card>
              ))}
            </Stack>
          </Container>
        </Box>
      </LoadScript>

      <Dialog
        open={!!deleteZone}
        onClose={() => setDeleteZone(null)}
        PaperProps={{
          sx: {
            backgroundColor: '#0f172a',
            color: '#fff',
            borderRadius: 3,
            boxShadow: 24
          }
        }}
      >
        <DialogTitle>Obriši zonu</DialogTitle>

        <DialogContent>
          <Typography>
            Da li si siguran da želiš obrisati zonu "{deleteZone?.name}"?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeleteZone(null)}
            sx={{ color: '#94a3b8' }}
          >
            Odustani
          </Button>

          <Button
            onClick={async () => {
              await axios.delete(`${SERVER_URL}/api/zonas/${deleteZone.id}`);
              setDeleteZone(null);
              getAllZones();
            }}
            sx={{
              backgroundColor: '#dc2626',
              color: '#fff',
              '&:hover': { backgroundColor: '#b91c1c' }
            }}
          >
            Obriši
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}